import { SubTask } from '../core/task-splitter';
import pc from 'picocolors';

export interface VisualizationOptions {
  format?: 'tree' | 'graph' | 'mermaid';
  showTime?: boolean;
  showPriority?: boolean;
  showCriticalPath?: boolean;
  criticalPath?: string[];
}

export class DependencyVisualizer {
  /**
   * Visualize task dependencies as tree
   */
  static visualizeTree(
    subtasks: SubTask[], 
    options: VisualizationOptions = {}
  ): string {
    const { showTime = true, showPriority = true, showCriticalPath = false, criticalPath = [] } = options;
    const lines: string[] = [];
    
    // Build dependency map
    const childrenMap = new Map<string, string[]>();
    const roots: string[] = [];
    
    // Find root tasks and build children map
    subtasks.forEach(task => {
      if (!task.dependencies || task.dependencies.length === 0) {
        roots.push(task.id);
      } else {
        task.dependencies.forEach(dep => {
          if (!childrenMap.has(dep)) {
            childrenMap.set(dep, []);
          }
          childrenMap.get(dep)!.push(task.id);
        });
      }
    });
    
    // Recursive function to build tree
    const buildTree = (taskId: string, indent: string, isLast: boolean, prefix: string = ''): void => {
      const task = subtasks.find(t => t.id === taskId);
      if (!task) return;
      
      // Build line
      let line = prefix;
      line += isLast ? '└── ' : '├── ';
      
      // Task title with formatting
      let title = task.title;
      if (showCriticalPath && criticalPath.includes(taskId)) {
        title = pc.red('⚡ ' + title);
      } else {
        title = pc.cyan(title);
      }
      line += title;
      
      // Add metadata
      const metadata: string[] = [];
      if (showTime && task.estimatedTime) {
        metadata.push(`${formatTime(task.estimatedTime)}`);
      }
      if (showPriority) {
        metadata.push(formatPriority(task.priority));
      }
      if (metadata.length > 0) {
        line += pc.dim(` (${metadata.join(', ')})`);
      }
      
      lines.push(line);
      
      // Process children
      const children = childrenMap.get(taskId) || [];
      children.forEach((childId, index) => {
        const isLastChild = index === children.length - 1;
        const childPrefix = prefix + (isLast ? '    ' : '│   ');
        buildTree(childId, indent, isLastChild, childPrefix);
      });
    };
    
    // Build trees from roots
    lines.push(pc.bold('Task Dependency Tree:\n'));
    roots.forEach((rootId, index) => {
      buildTree(rootId, '', index === roots.length - 1);
    });
    
    return lines.join('\n');
  }

  /**
   * Visualize as ASCII graph
   */
  static visualizeGraph(
    subtasks: SubTask[], 
    options: VisualizationOptions = {}
  ): string {
    const { showCriticalPath = false, criticalPath = [] } = options;
    const lines: string[] = [];
    const nodeWidth = 25;
    const nodeHeight = 3;
    
    // Calculate positions using topological sort
    const positions = this.calculatePositions(subtasks);
    
    // Create grid
    const maxRow = Math.max(...Array.from(positions.values()).map(p => p.row));
    const maxCol = Math.max(...Array.from(positions.values()).map(p => p.col));
    
    const grid: string[][] = [];
    for (let i = 0; i <= maxRow * nodeHeight + nodeHeight; i++) {
      grid[i] = new Array((maxCol + 1) * nodeWidth).fill(' ');
    }
    
    // Draw nodes
    subtasks.forEach(task => {
      const pos = positions.get(task.id);
      if (!pos) return;
      
      const startRow = pos.row * nodeHeight;
      const startCol = pos.col * nodeWidth;
      
      // Draw box
      const box = this.createBox(task, nodeWidth - 2, showCriticalPath && criticalPath.includes(task.id));
      box.forEach((line, i) => {
        for (let j = 0; j < line.length; j++) {
          if (startRow + i < grid.length && startCol + j < grid[0].length) {
            grid[startRow + i][startCol + j] = line[j];
          }
        }
      });
    });
    
    // Draw connections
    subtasks.forEach(task => {
      if (!task.dependencies) return;
      
      const toPos = positions.get(task.id);
      if (!toPos) return;
      
      task.dependencies.forEach(depId => {
        const fromPos = positions.get(depId);
        if (!fromPos) return;
        
        // Simple arrow drawing
        const fromRow = fromPos.row * nodeHeight + 1;
        const fromCol = (fromPos.col + 1) * nodeWidth - 3;
        const toRow = toPos.row * nodeHeight + 1;
        const toCol = toPos.col * nodeWidth + 1;
        
        // Draw horizontal line
        if (fromRow === toRow) {
          for (let col = fromCol; col < toCol; col++) {
            grid[fromRow][col] = '─';
          }
          grid[fromRow][toCol - 1] = '►';
        } else {
          // Draw vertical connection
          const midCol = fromCol + 2;
          grid[fromRow][fromCol] = '─';
          grid[fromRow][midCol] = '┐';
          for (let row = fromRow + 1; row < toRow; row++) {
            grid[row][midCol] = '│';
          }
          grid[toRow][midCol] = '└';
          for (let col = midCol + 1; col < toCol; col++) {
            grid[toRow][col] = '─';
          }
          grid[toRow][toCol - 1] = '►';
        }
      });
    });
    
    // Convert grid to string
    lines.push(pc.bold('Task Dependency Graph:\n'));
    grid.forEach(row => {
      lines.push(row.join(''));
    });
    
    return lines.join('\n');
  }

  /**
   * Generate Mermaid diagram
   */
  static visualizeMermaid(
    subtasks: SubTask[], 
    options: VisualizationOptions = {}
  ): string {
    const { showTime = true, showCriticalPath = false, criticalPath = [] } = options;
    const lines: string[] = [];
    
    lines.push('```mermaid');
    lines.push('graph TD');
    
    // Define nodes
    subtasks.forEach(task => {
      let label = task.title;
      if (showTime && task.estimatedTime) {
        label += `\\n${formatTime(task.estimatedTime)}`;
      }
      
      const nodeStyle = showCriticalPath && criticalPath.includes(task.id) 
        ? ':::critical' 
        : '';
      
      lines.push(`    ${task.id}["${label}"]${nodeStyle}`);
    });
    
    // Define edges
    subtasks.forEach(task => {
      if (task.dependencies) {
        task.dependencies.forEach(depId => {
          lines.push(`    ${depId} --> ${task.id}`);
        });
      }
    });
    
    // Add styles
    if (showCriticalPath && criticalPath.length > 0) {
      lines.push('    classDef critical fill:#f96,stroke:#333,stroke-width:3px;');
    }
    
    lines.push('```');
    
    return lines.join('\n');
  }

  /**
   * Calculate node positions for graph layout
   */
  private static calculatePositions(subtasks: SubTask[]): Map<string, { row: number, col: number }> {
    const positions = new Map<string, { row: number, col: number }>();
    const levels = new Map<string, number>();
    
    // Calculate levels using topological sort
    const visited = new Set<string>();
    
    const calculateLevel = (taskId: string): number => {
      if (levels.has(taskId)) {
        return levels.get(taskId)!;
      }
      
      const task = subtasks.find(t => t.id === taskId);
      if (!task) return 0;
      
      if (!task.dependencies || task.dependencies.length === 0) {
        levels.set(taskId, 0);
        return 0;
      }
      
      let maxDepLevel = 0;
      task.dependencies.forEach(depId => {
        maxDepLevel = Math.max(maxDepLevel, calculateLevel(depId));
      });
      
      const level = maxDepLevel + 1;
      levels.set(taskId, level);
      return level;
    };
    
    // Calculate levels for all tasks
    subtasks.forEach(task => calculateLevel(task.id));
    
    // Group tasks by level
    const levelGroups = new Map<number, string[]>();
    levels.forEach((level, taskId) => {
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level)!.push(taskId);
    });
    
    // Assign positions
    levelGroups.forEach((tasks, level) => {
      tasks.forEach((taskId, index) => {
        positions.set(taskId, { row: level, col: index });
      });
    });
    
    return positions;
  }

  /**
   * Create a box for graph visualization
   */
  private static createBox(task: SubTask, width: number, isCritical: boolean): string[] {
    const lines: string[] = [];
    const title = task.title.slice(0, width - 4);
    const time = formatTime(task.estimatedTime);
    
    const borderColor = isCritical ? pc.red : pc.cyan;
    const textColor = isCritical ? pc.red : pc.white;
    
    // Top border
    lines.push(borderColor('┌' + '─'.repeat(width - 2) + '┐'));
    
    // Title line
    const titlePadding = Math.floor((width - 2 - title.length) / 2);
    lines.push(
      borderColor('│') + 
      ' '.repeat(titlePadding) + 
      textColor(title) + 
      ' '.repeat(width - 2 - titlePadding - title.length) + 
      borderColor('│')
    );
    
    // Time line
    const timePadding = Math.floor((width - 2 - time.length) / 2);
    lines.push(
      borderColor('│') + 
      ' '.repeat(timePadding) + 
      pc.dim(time) + 
      ' '.repeat(width - 2 - timePadding - time.length) + 
      borderColor('│')
    );
    
    // Bottom border
    lines.push(borderColor('└' + '─'.repeat(width - 2) + '┘'));
    
    return lines;
  }
}

// Helper functions
function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatPriority(priority: string): string {
  const icons = {
    high: '🔴',
    medium: '🟡',
    low: '🟢'
  };
  return icons[priority] || priority;
}